import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { LogIn, Mail, Lock, User, ArrowRight, Loader2 } from "lucide-react";
import { track } from "@/lib/analytics";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { consumeRef, peekRef } from "@/lib/referral";
import { useT, tr } from "@/lib/i18n";

const formatErr = (detail) => {
  if (detail == null) return tr("Something went wrong. Please try again.", "Une erreur s'est produite. Veuillez réessayer.");
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const Login = () => {
  useT();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // "login" | "register" | "forgot"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/projects";
    track("signup_click", { method: "google" });
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") {
        const r = await api.post("/auth/login", { email, password });
        setUser(r.data);
        track("signup_click", { method: "email_login" });
        toast.success(tr("Welcome back", "Bon retour parmi nous"));
        navigate("/projects");
      } else if (mode === "register") {
        if (password.length < 8 || !/\d/.test(password)) {
          setErr(tr("Password must be at least 8 characters and include a number.", "Le mot de passe doit comporter au moins 8\u00a0caractères, dont un chiffre."));
          setBusy(false);
          return;
        }
        const r = await api.post("/auth/register", {
          email,
          password,
          name,
          referrer_code: consumeRef() || undefined,
        });
        setUser(r.data);
        track("signup_click", { method: "email_register" });
        toast.success(tr("Account created — check your inbox for the welcome email", "Compte créé — consultez votre boîte de réception pour l'e-mail de bienvenue"));
        navigate("/projects");
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        toast.success(tr("If that email is on file, we've sent a reset link", "Si cette adresse est enregistrée, nous avons envoyé un lien de réinitialisation"));
        setMode("login");
      }
    } catch (e2) {
      const msg = formatErr(e2?.response?.data?.detail) || e2?.message;
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  const title = mode === "register" ? tr("Create your account", "Créez votre compte") : mode === "forgot" ? tr("Reset your password", "Réinitialisez votre mot de passe") : tr("Sign in to your account", "Connectez-vous à votre compte");
  const subtitle = mode === "register"
    ? tr("Set a password to save projects and unlock reports.", "Choisissez un mot de passe pour enregistrer vos projets et déverrouiller vos rapports.")
    : mode === "forgot"
    ? tr("We'll send a reset link to your professional email.", "Nous enverrons un lien de réinitialisation à votre adresse e-mail professionnelle.")
    : tr("Save projects, buy reports, and share them with your team.", "Enregistrez vos projets, achetez des rapports et partagez-les avec votre équipe.");

  return (
    <div className="App">
      <Header />
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-16 md:py-20">
        <div className="max-w-lg">
          <div className="overline mb-3">{tr("Access", "Accès")}</div>
          <h1 className="font-display text-4xl md:text-5xl leading-[1.05]">{title}</h1>
          <p className="mt-4 text-base" style={{ color: "var(--muted)" }}>{subtitle}</p>

          {/* Google sign-in */}
          <button onClick={googleSignIn} className="btn-primary mt-8 w-full justify-center" data-testid="google-signin">
            <LogIn size={16} /> {tr("Continue with Google", "Continuer avec Google")}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 my-7">
            <div className="flex-1 h-px" style={{ background: "var(--hairline)" }} />
            <div className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>
              {tr("Or continue with email", "Ou continuer avec un e-mail")}
            </div>
            <div className="flex-1 h-px" style={{ background: "var(--hairline)" }} />
          </div>

          {/* Email form */}
          <form onSubmit={submit} className="space-y-3" data-testid="email-auth-form">
            {mode === "register" && (
              <label className="block">
                <div className="text-xs mb-1 uppercase tracking-wider" style={{ color: "var(--muted)" }}>{tr("Full name", "Nom complet")}</div>
                <div className="relative">
                  <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--muted)" }} />
                  <input
                    type="text" required autoComplete="name" value={name} onChange={(e) => setName(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 text-sm"
                    style={{ background: "var(--white)", border: "1px solid var(--hairline)" }}
                    data-testid="input-name"
                  />
                </div>
              </label>
            )}
            <label className="block">
              <div className="text-xs mb-1 uppercase tracking-wider" style={{ color: "var(--muted)" }}>{tr("Professional email", "E-mail professionnel")}</div>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--muted)" }} />
                <input
                  type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 text-sm"
                  style={{ background: "var(--white)", border: "1px solid var(--hairline)" }}
                  data-testid="input-email"
                />
              </div>
            </label>
            {mode !== "forgot" && (
              <label className="block">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs uppercase tracking-wider" style={{ color: "var(--muted)" }}>{tr("Password", "Mot de passe")}</div>
                  {mode === "login" && (
                    <button type="button" onClick={() => { setErr(""); setMode("forgot"); }}
                            className="text-xs" style={{ color: "var(--brand-green)" }} data-testid="link-forgot">
                      {tr("Forgot password?", "Mot de passe oublié\u00a0?")}
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--muted)" }} />
                  <input
                    type="password" required minLength={8}
                    autoComplete={mode === "register" ? "new-password" : "current-password"}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 text-sm"
                    style={{ background: "var(--white)", border: "1px solid var(--hairline)" }}
                    data-testid="input-password"
                  />
                </div>
                {mode === "register" && (
                  <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>{tr("Minimum 8 characters, must include a number.", "8\u00a0caractères minimum, dont un chiffre.")}</div>
                )}
              </label>
            )}

            {err && (
              <div className="text-sm p-3" style={{ background: "rgba(184,50,50,0.08)", color: "#B83232", border: "1px solid rgba(184,50,50,0.2)" }} data-testid="auth-error">
                {err}
              </div>
            )}

            <button type="submit" className="btn-primary w-full justify-center" disabled={busy} data-testid={`submit-${mode}`}>
              {busy ? <Loader2 className="animate-spin" size={14} /> : <ArrowRight size={14} />}
              {mode === "register" ? tr("Create account", "Créer le compte") : mode === "forgot" ? tr("Send reset link", "Envoyer le lien") : tr("Sign in with email", "Se connecter avec un e-mail")}
            </button>
          </form>

          {/* Mode switch */}
          <div className="mt-5 text-sm text-center" style={{ color: "var(--muted)" }}>
            {mode === "login" && (
              <>{tr("New here?", "Nouveau\u00a0?")}{" "}
                <button type="button" onClick={() => { setErr(""); setMode("register"); }} style={{ color: "var(--brand-green)" }} data-testid="link-register">
                  {tr("Create a professional account", "Créer un compte professionnel")}
                </button>
              </>
            )}
            {mode === "register" && (
              <>{tr("Already have an account?", "Vous avez déjà un compte\u00a0?")}{" "}
                <button type="button" onClick={() => { setErr(""); setMode("login"); }} style={{ color: "var(--brand-green)" }} data-testid="link-signin">
                  {tr("Sign in", "Se connecter")}
                </button>
              </>
            )}
            {mode === "forgot" && (
              <button type="button" onClick={() => { setErr(""); setMode("login"); }} style={{ color: "var(--brand-green)" }} data-testid="link-back-to-signin">
                {tr("← Back to sign in", "← Retour à la connexion")}
              </button>
            )}
          </div>

          <p className="text-xs mt-8 font-mono" style={{ color: "var(--muted)" }}>
            {tr("SESSION LASTS 7 DAYS · SIGN OUT ANYTIME", "SESSION VALABLE 7\u00a0JOURS · DÉCONNEXION À TOUT MOMENT")}
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};
